'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { formatPrice, CurrencyCode } from '@/lib/travelpayouts';

interface HotelResult {
  id: string;
  name: string;
  location: string;
  stars: number;
  rating: number;
  reviewCount: number;
  reviewLabel: string;
  pricePerNight: number;
  totalPrice: number;
  originalPrice?: number;
  badge?: string;
  badgeType?: 'best' | 'cheapest' | 'popular';
  priceTrend: 'up' | 'down' | 'stable';
  priceTrendAmount?: number;
  amenities: string[];
  image: string;
  imageAlt: string;
  partner: string;
  distanceFromCenter: string;
  freeCancellation: boolean;
  breakfastIncluded: boolean;
}

const hotelResults: HotelResult[] = [
{
  id: 'hotel-001',
  name: 'The Savoy',
  location: 'Strand, Central London',
  stars: 5,
  rating: 9.3,
  reviewCount: 4821,
  reviewLabel: 'Exceptional',
  pricePerNight: 485,
  totalPrice: 3395,
  originalPrice: 4900,
  badge: 'Best Value',
  badgeType: 'best',
  priceTrend: 'down',
  priceTrendAmount: 78,
  amenities: ['Free WiFi', 'Pool', 'Spa', 'Restaurant', 'Gym'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1513f5fa2-1772142063708.png",
  imageAlt: 'Luxury hotel lobby with grand chandelier, marble floors and elegant furnishings',
  partner: 'Booking.com',
  distanceFromCenter: '0.3 km from City Centre',
  freeCancellation: true,
  breakfastIncluded: false
},
{
  id: 'hotel-002',
  name: 'citizenM London Bankside',
  location: 'Bankside, South Bank',
  stars: 4,
  rating: 8.8,
  reviewCount: 2340,
  reviewLabel: 'Excellent',
  pricePerNight: 148,
  totalPrice: 1036,
  badge: 'Cheapest',
  badgeType: 'cheapest',
  priceTrend: 'down',
  priceTrendAmount: 22,
  amenities: ['Free WiFi', 'Gym', 'Bar', 'Self Check-in'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dbef7651-1772362041239.png",
  imageAlt: 'Modern hotel room with large window view of city skyline and minimalist design',
  partner: 'Hotels.com',
  distanceFromCenter: '1.2 km from City Centre',
  freeCancellation: true,
  breakfastIncluded: false
},
{
  id: 'hotel-003',
  name: 'The Ned London',
  location: 'City of London',
  stars: 5,
  rating: 9.1,
  reviewCount: 3102,
  reviewLabel: 'Exceptional',
  pricePerNight: 312,
  totalPrice: 2184,
  originalPrice: 2800,
  priceTrend: 'down',
  priceTrendAmount: 44,
  amenities: ['Free WiFi', 'Pool', 'Spa', 'Multiple Restaurants', 'Gym', 'Bar'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffa8b0c6-1772201883500.png",
  imageAlt: 'Historic London hotel exterior with neoclassical columns and ornate stone facade',
  partner: 'Expedia',
  distanceFromCenter: '0.8 km from City Centre',
  freeCancellation: true,
  breakfastIncluded: true
},
{
  id: 'hotel-004',
  name: 'Premier Inn London City',
  location: 'Aldgate, East London',
  stars: 3,
  rating: 7.9,
  reviewCount: 5670,
  reviewLabel: 'Good',
  pricePerNight: 89,
  totalPrice: 623,
  priceTrend: 'up',
  priceTrendAmount: 12,
  amenities: ['Free WiFi', 'Restaurant', 'Bar'],
  image: "https://images.unsplash.com/photo-1699298932933-46ef66feea33",
  imageAlt: 'Clean modern hotel room with double bed, neutral decor and city view window',
  partner: 'Premier Inn',
  distanceFromCenter: '2.1 km from City Centre',
  freeCancellation: false,
  breakfastIncluded: false
},
{
  id: 'hotel-005',
  name: 'Claridge\'s',
  location: 'Mayfair, West End',
  stars: 5,
  rating: 9.5,
  reviewCount: 1890,
  reviewLabel: 'Exceptional',
  pricePerNight: 620,
  totalPrice: 4340,
  badge: 'Popular',
  badgeType: 'popular',
  priceTrend: 'stable',
  amenities: ['Free WiFi', 'Spa', 'Fine Dining', 'Concierge', 'Gym', 'Bar'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c3da9418-1772968522209.png",
  imageAlt: 'Elegant Art Deco hotel suite with plush furnishings, tall windows and golden lighting',
  partner: 'Agoda',
  distanceFromCenter: '0.5 km from City Centre',
  freeCancellation: true,
  breakfastIncluded: true
},
{
  id: 'hotel-006',
  name: 'Park Plaza Westminster Bridge',
  location: 'Westminster, Central London',
  stars: 4,
  rating: 8.4,
  reviewCount: 6230,
  reviewLabel: 'Very Good',
  pricePerNight: 198,
  totalPrice: 1386,
  originalPrice: 1680,
  priceTrend: 'down',
  priceTrendAmount: 34,
  amenities: ['Free WiFi', 'Pool', 'Gym', 'Restaurant', 'Spa'],
  image: "https://images.unsplash.com/photo-1673687781946-e0570d6d1f32",
  imageAlt: 'Hotel swimming pool with blue water, lounge chairs and floor-to-ceiling windows',
  partner: 'Trip.com',
  distanceFromCenter: '0.6 km from City Centre',
  freeCancellation: true,
  breakfastIncluded: false
}];


function StarRating({ stars }: {stars: number;}) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) =>
      <Icon
        key={`star-${i}`}
        name="StarIcon"
        variant={i < stars ? 'solid' : 'outline'}
        size={12}
        className={i < stars ? 'star-filled' : 'text-muted'} />

      )}
    </div>);

}

function RatingBadge({ rating, label }: {rating: number;label: string;}) {
  const color =
  rating >= 9 ? 'bg-positive text-white' : rating >= 8 ? 'bg-primary text-white' : 'bg-secondary text-primary';
  return (
    <div className="flex items-center gap-1.5">
      <span className={`text-sm font-800 px-2 py-0.5 rounded-lg tabular-nums ${color}`}>
        {rating.toFixed(1)}
      </span>
      <span className="text-xs font-semibold text-foreground">{label}</span>
    </div>);

}

export default function HotelResultsList({ sortBy, currency = 'USD' }: {sortBy: string;currency?: CurrencyCode;}) {
  const [wishlisted, setWishlisted] = useState<Set<string>>(new Set());
  const [redirecting, setRedirecting] = useState<string | null>(null);

  const toggleWishlist = (id: string) => {
    setWishlisted((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else
      next.add(id);
      return next;
    });
  };

  const handleViewDeal = (id: string) => {
    // BACKEND: Track affiliate click event, then redirect to partner hotel URL
    setRedirecting(id);
    setTimeout(() => setRedirecting(null), 1500);
  };

  const sorted = [...hotelResults].sort((a, b) => {
    if (sortBy === 'price-asc') return a.pricePerNight - b.pricePerNight;
    if (sortBy === 'price-desc') return b.pricePerNight - a.pricePerNight;
    if (sortBy === 'rating-desc') return b.rating - a.rating;
    return 0;
  });

  return (
    <div className="space-y-3 fade-in">
      {sorted.map((hotel) =>
      <div
        key={hotel.id}
        className="bg-card border border-border rounded-2xl overflow-hidden result-card-hover">
        
          <div className="flex flex-col sm:flex-row">
            {/* Image */}
            <div className="relative w-full sm:w-48 h-44 sm:h-auto flex-shrink-0">
              <AppImage
              src={hotel.image}
              alt={hotel.imageAlt}
              fill
              sizes="(max-width: 640px) 100vw, 192px"
              className="object-cover" />
            
              <button
              type="button"
              onClick={() => toggleWishlist(hotel.id)}
              aria-label={wishlisted.has(hotel.id) ? 'Remove from wishlist' : 'Save to wishlist'}
              className={`absolute top-2 right-2 p-2 rounded-full backdrop-blur-sm transition-all wishlist-btn ${
              wishlisted.has(hotel.id) ?
              'bg-negative/90 text-white' : 'bg-black/30 text-white hover:bg-negative/90'}`
              }>
              
                <Icon
                name="HeartIcon"
                variant={wishlisted.has(hotel.id) ? 'solid' : 'outline'}
                size={16} />
              
              </button>
              {hotel.badge &&
            <span
              className={`absolute bottom-2 left-2 text-xs font-semibold px-2 py-0.5 rounded-full ${
              hotel.badgeType === 'best' ? 'price-badge-best' :
              hotel.badgeType === 'cheapest' ? 'price-badge-cheapest' : 'price-badge-popular'}`
              }>
              
                  {hotel.badge}
                </span>
            }
            </div>```tsx
            {/* Hotel Info */}
            <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StarRating stars={hotel.stars} />
                    <span className="text-xs text-muted-foreground">{hotel.stars}-star hotel</span>
                  </div>
                  <h3 className="text-base font-700 text-foreground mb-0.5 truncate">{hotel.name}</h3>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="MapPinIcon" size={12} />
                    {hotel.location} · {hotel.distanceFromCenter}
                  </p>

                  {/* Amenities */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {hotel.amenities.slice(0, 4).map((amenity) =>
                  <span
                    key={`${hotel.id}-amenity-${amenity}`}
                    className="text-xs bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">
                    
                        {amenity}
                      </span>
                  )}
                    {hotel.amenities.length > 4 &&
                  <span className="text-xs text-primary font-medium">
                        +{hotel.amenities.length - 4} more
                      </span>
                  }
                  </div>

                  {/* Perks */}
                  <div className="flex items-center gap-3 mt-2">
                    {hotel.freeCancellation &&
                  <span className="text-xs text-positive font-semibold flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={12} />
                        Free cancellation
                      </span>
                  }
                    {hotel.breakfastIncluded &&
                  <span className="text-xs text-accent font-semibold flex items-center gap-1">
                        <Icon name="CheckCircleIcon" size={12} />
                        Breakfast included
                      </span>
                  }
                  </div>
                </div>

                {/* Rating + Price + CTA */}
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <RatingBadge rating={hotel.rating} label={hotel.reviewLabel} />
                  <p className="text-xs text-muted-foreground">
                    {hotel.reviewCount.toLocaleString()} reviews
                  </p>

                  <div className="text-right mt-1">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-800 text-foreground tabular-nums">
                        {formatPrice(hotel.pricePerNight, currency)}
                      </span>
                      <span className="text-xs text-muted-foreground">/night</span>
                    </div>
                    {hotel.originalPrice &&
                  <p className="text-xs text-muted-foreground line-through tabular-nums">
                        {formatPrice(Math.round(hotel.originalPrice / 7), currency)}/night
                      </p>
                  }
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {formatPrice(hotel.totalPrice, currency)} total · 7 nights
                    </p>
                    {hotel.priceTrend !== 'stable' && hotel.priceTrendAmount &&
                  <p
                    className={`text-xs font-semibold flex items-center gap-0.5 justify-end mt-0.5 ${
                    hotel.priceTrend === 'down' ? 'price-trend-down' : 'price-trend-up'}`
                    }>
                    
                        <Icon
                      name={
                      hotel.priceTrend === 'down' ?
                      'ArrowTrendingDownIcon' : 'ArrowTrendingUpIcon'
                      }
                      size={12} />
                    
                        {hotel.priceTrend === 'down' ? '-' : '+'}
                        {formatPrice(hotel.priceTrendAmount, currency)} vs avg
                      </p>
                  }
                  </div>

                  <button
                  type="button"
                  onClick={() => handleViewDeal(hotel.id)}
                  className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2 min-w-[130px] justify-center"
                  disabled={redirecting === hotel.id}>
                  
                    {redirecting === hotel.id ?
                  <>
                        <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                        Loading...
                      </> :

                  <>
                        View Deal
                        <Icon name="ArrowTopRightOnSquareIcon" size={14} />
                      </>
                  }
                  </button>
                  <p className="text-xs text-muted-foreground">via {hotel.partner}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Load more */}
      <div className="text-center pt-4">
        <button type="button" className="btn-secondary px-8 py-3 text-sm">
          Show 12 more hotels
        </button>
        <p className="text-xs text-muted-foreground mt-2">
          Prices include taxes and fees. Final price confirmed at partner site.
        </p>
      </div>
    </div>);

}